import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, mergeMap, of, throwError } from 'rxjs';
import { TranslationHelperService } from '../services/translation-helper.service';
import { ParsedError } from '../error/models/error-response.model';

export const errorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const translationHelper = inject(TranslationHelperService);

  const parseError = (error: HttpErrorResponse): ParsedError => {
    const statusCode = error.status;
    let message = '';
    let errors: string[] = [];
    let correlationId: string | undefined;
    let problemDetails;

    if (error.error && typeof error.error === 'object') {
      const errorBody = error.error;

      if (errorBody.CorrelationId || errorBody.correlationId) {
        correlationId = errorBody.CorrelationId || errorBody.correlationId;
      }

      if (
        errorBody.success === false &&
        Array.isArray(errorBody.errors) &&
        errorBody.errors.length > 0
      ) {
        errors = errorBody.errors;
        errors = errors.flatMap((err) =>
          err.split('|').map((e: string) => e.trim())
        );
        message = errors.join('\n');
      } else if (
        Array.isArray(errorBody.Errors) &&
        errorBody.Errors.length > 0
      ) {
        errors = errorBody.Errors;
        if (!message) {
          message = errors[0];
        }
      } else if (Array.isArray(errorBody.error)) {
        errors = errorBody.error;
        message = errors.join('\n');
      } else if (errorBody.ProblemDetails || errorBody.problemDetails) {
        problemDetails = errorBody.ProblemDetails || errorBody.problemDetails;
        if (problemDetails.detail || problemDetails.Detail) {
          message = problemDetails.detail || problemDetails.Detail;
        } else if (problemDetails.title || problemDetails.Title) {
          message = problemDetails.title || problemDetails.Title;
        }
      } else if (errorBody.message && typeof errorBody.message === 'string') {
        message = errorBody.message.trim();
        errors = [message];
      }
    }

    if (!message) {
      message = getDefaultErrorMessage(statusCode, error);
      errors = [message];
    }

    return {
      statusCode,
      message,
      errors,
      correlationId,
      problemDetails,
    };
  };

  const getDefaultErrorMessage = (
    statusCode: number,
    error: HttpErrorResponse
  ): string => {
    switch (statusCode) {
      case HttpStatusCode.Unauthorized: {
        const refreshTokenUrl = error.url?.includes('RefreshToken');
        if (refreshTokenUrl) {
          return translationHelper.translate('GLOBAL.UNAUTHORIZED');
        }
        return translationHelper.translate('GLOBAL.INVALID_VALUE');
      }

      case HttpStatusCode.Forbidden:
        return translationHelper.translate('GLOBAL.FORBIDDEN');

      case HttpStatusCode.NotFound:
        return translationHelper.translate('GLOBAL.NOT_FOUND');

      case HttpStatusCode.BadRequest:
        return translationHelper.translate('GLOBAL.BAD_REQUEST');

      case HttpStatusCode.InternalServerError:
      case HttpStatusCode.NotImplemented:
      case HttpStatusCode.BadGateway:
      case HttpStatusCode.ServiceUnavailable:
      case HttpStatusCode.GatewayTimeout:
      case HttpStatusCode.HttpVersionNotSupported:
      case HttpStatusCode.VariantAlsoNegotiates:
      case HttpStatusCode.InsufficientStorage:
      case HttpStatusCode.LoopDetected:
      case HttpStatusCode.NotExtended:
      case HttpStatusCode.NetworkAuthenticationRequired:
        return translationHelper.translate('GLOBAL.SERVER_ERROR');

      case 0:
        return translationHelper.translate('GLOBAL.NETWORK_ERROR');

      default:
        return translationHelper.translate('GLOBAL.INVALID_VALUE');
    }
  };

  const handleHttpError = (error: HttpErrorResponse): void => {
    const parsedError = parseError(error);

    messageService.add({
      severity: 'error',
      summary: translationHelper.translate('GLOBAL.ERROR_TITLE'),
      detail: parsedError.message,
      life: 7000,
    });
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      return of(error).pipe(
        mergeMap((err) => {
          handleHttpError(err);
          return throwError(() => err);
        })
      );
    })
  );
};
